import { apiFetch } from '../api-client';
import { isMockMode } from '../mock/payments-mock';

export interface EmployeePickerItem {
  id: string;
  code?: string;
  name: string;
  email?: string;
  position?: string | null;
  departmentName?: string | null;
  active?: boolean;
}

interface PickerResponse {
  items: EmployeePickerItem[];
  syncMeta?: { lastSyncedAt: string | null; totalActive: number };
  paging?: { total: number };
}

const MOCK_EMPLOYEES: EmployeePickerItem[] = [
  { id: 'dev-ktt', code: 'HG001', name: 'Nguyễn Thị Hà', email: 'hant@hitek.vn', position: 'Kế toán trưởng', departmentName: 'Kế toán', active: true },
  { id: 'dev-nvkt', code: 'HG002', name: 'Lê Huỳnh Diệu', email: 'dieulh@hitek.vn', position: 'NV Kế toán', departmentName: 'Kế toán', active: true },
  { id: 'dev-ceo', code: 'HG003', name: 'Phạm Minh Đức', email: 'ducpm@hitek.vn', position: 'CEO', departmentName: 'Ban Giám đốc', active: true },
  { id: 'dev-nv', code: 'HG004', name: 'Trần Thị B', email: 'btt@hitek.vn', position: 'Marketing Lead', departmentName: 'Marketing', active: true },
];

export interface Employee {
  id: string;
  code: string;
  name: string;
  email: string;
  position: string | null;
  departmentId: string | null;
  departmentName: string | null;
  managerId: string | null;
  employmentType: string | null;
  active: boolean;
  phone: string | null;

  // HR sync
  cccd: string | null;
  bhxhBookNumber: string | null;
  bhStartDate: string | null;
  hireDate: string | null;

  // Probation
  probationStart: string | null;
  probationEnd: string | null;
  probationSalaryPct: number | null;

  // Salary
  agreedSalary: number | null;
  totalIncome: number | null;
  salaryType: string | null;
  salaryPct: number | null;
  effectiveFrom: string | null;
  effectiveNote: string | null;
  salaryHistory: EmployeeSalaryHistory[];

  // Bank + tax
  costAccountCode: string | null;
  bankName: string | null;
  bankAccount: string | null;
  bankBranch: string | null;
  taxCode: string | null;
  dependentsCount: number;
  socialInsuranceBase: number | null;

  // Per-employee allowance overrides (subset of active SalaryAllowances)
  allowances: EmployeeAllowanceInput[];
}

export interface EmployeeSalaryHistory {
  id: string;
  agreedSalary: number;
  allowanceTotal: number;
  socialInsuranceBase: number | null;
  salaryType: string | null;
  salaryPct: number | null;
  effectiveFrom: string;
  note: string | null;
  changedBy: string | null;
  createdAt: string;
}

export interface EmployeeAllowanceInput {
  code: string;
  amount: number;
}

export interface HrCandidate {
  hrEmployeeId: string;
  code: string;
  name: string;
  email: string;
  position: string;
  departmentName: string;
  employmentType: 'CT' | 'TV' | 'DV';
  hireDate: string;
  cccd?: string;
  phone?: string;
  bhxhBookNumber?: string;
  bhStartDate?: string;
}

export interface CreateEmployeeInput {
  /** Chọn NV từ HR directory — auto-fill identity, chỉ nhập salary/bank/tax config */
  hrCandidateCode?: string;
  code?: string;
  name?: string;
  email?: string;
  position?: string;
  departmentName?: string;
  employmentType?: string;
  phone?: string;
  cccd?: string;
  bhxhBookNumber?: string;
  bhStartDate?: string;
  hireDate?: string;
  probationStart?: string;
  probationEnd?: string;
  probationSalaryPct?: number;
  agreedSalary?: number;
  salaryType?: string;
  salaryPct?: number;
  effectiveFrom?: string;
  effectiveNote?: string;
  bankName?: string;
  bankAccount?: string;
  bankBranch?: string;
  taxCode?: string;
  dependentsCount?: number;
  socialInsuranceBase?: number;
  allowances?: EmployeeAllowanceInput[];
}

export interface EmployeeListItem {
  id: string;
  code: string;
  name: string;
  email: string;
  position: string | null;
  departmentName: string | null;
  employmentType: string | null;
  active: boolean;
  hireDate: string | null;
  probationEnd: string | null;
  probationSalaryPct: number | null;
  agreedSalary: number | null;
  totalIncome: number | null;
  dependentsCount: number;
}

export interface EmployeeListResponse {
  items: EmployeeListItem[];
  syncMeta?: { lastSyncedAt: string | null; totalActive: number };
  paging?: { total: number; page: number; limit: number; totalPages: number };
}

export const employeesApi = {
  list: (search?: string) => {
    const q = search ? `?search=${encodeURIComponent(search)}&limit=200` : '?limit=200';
    return apiFetch<EmployeeListResponse>(`/api/employees${q}`);
  },
  hrDirectory: (search?: string) => {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return apiFetch<{ items: HrCandidate[] }>(`/api/employees/hr-directory${q}`);
  },
  findOne: (id: string) => apiFetch<Employee>(`/api/employees/${id}`),
  create: (input: CreateEmployeeInput) =>
    apiFetch<Employee>('/api/employees', { method: 'POST', body: JSON.stringify(input) }),
  update: (id: string, input: Partial<CreateEmployeeInput> & { active?: boolean }) =>
    apiFetch<Employee>(`/api/employees/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  async picker(search?: string): Promise<EmployeePickerItem[]> {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    try {
      // Ưu tiên dữ liệu nhân sự đã đồng bộ từ HR/Base để các form nghiệp vụ
      // (gán TSCĐ, chọn người nhận...) luôn dùng cùng một định danh nhân viên.
      const res = await apiFetch<PickerResponse>(`/api/employees/picker${q}`);
      return res.items;
    } catch (error) {
      if (!isMockMode()) throw error;
      const s = search?.toLowerCase();
      return MOCK_EMPLOYEES.filter((e) => !s || e.name.toLowerCase().includes(s) || (e.email || '').toLowerCase().includes(s) || (e.code || '').toLowerCase().includes(s));
    }
  },
};
